import { Header } from '@/pages/infant/infant-profile/components';
import P1 from '@/assets/pillar/p1.svg';
import {
  ActionModal,
  Alert,
  DialogPosition,
  FormInput,
  renderIcon,
  Typography,
} from '@ecdlink/ui';
import { useEffect, useMemo, useState } from 'react';
import { getAgeInYearsMonthsAndDays, useDialog } from '@ecdlink/core';
import { DynamicFormProps, Question } from '../../../dynamic-form';
import { useForm, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  InfantRoadToHealthModel,
  infantRoadToHealthModelSchema,
} from '@/schemas/infant/infant-road-to-health';
import { activitiesColours } from '../../../../activities-list';
import { differenceInMonths } from 'date-fns';

export const weightLengthAndHeightFormQuestions = {
  weight: 'Weight',
  length: 'Length',
  height: 'Height',
};
export const weightAndLengthFormSection = 'Growth monitoring (Weight & length)';

export const WeightAndLengthFormStep = ({
  infant,
  setEnableButton,
  setSectionQuestions,
}: DynamicFormProps) => {
  const {
    register,
    watch,
    control: infantRoadToHealthFormControl,
  } = useForm<InfantRoadToHealthModel>({
    resolver: yupResolver(infantRoadToHealthModelSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  const weightRegex =
    /^(1\.[5-9]\d{0,2}|[2-9](?:\.\d{1,3})?|1[0-9](?:\.\d{1,3})?|2[0-9](?:\.\d{1,3})?|30(?:\.0{1,3})?)$/;

  const { lengthAtBirth, weightAtBirth, height } = watch();

  const { errors } = useFormState({
    control: infantRoadToHealthFormControl,
  });

  const name = useMemo(
    () => infant?.firstName ?? infant?.user?.firstName ?? '',
    [infant]
  );
  const dateOfBirth = infant?.user?.dateOfBirth as string;

  const ageInMonths = differenceInMonths(new Date(), new Date(dateOfBirth));
  const { years: ageInYears } = getAgeInYearsMonthsAndDays(dateOfBirth);

  const isDisplayLength = useMemo(
    () => ageInMonths >= 6 && ageInMonths < 15,
    [ageInMonths]
  );
  const isDisplayHeight = useMemo(
    () => ageInMonths >= 24 && ageInYears < 5,
    [ageInMonths, ageInYears]
  );

  const isCheckedWeight = useMemo(() => {
    const formattedValue = weightAtBirth?.toString().replaceAll(',', '.');
    if (!formattedValue) return false;
    const isValidWeight =
      parseFloat(formattedValue) >= 1.5 && parseFloat(formattedValue) <= 30;
    return formattedValue && formattedValue?.length < 5 && isValidWeight;
  }, [weightAtBirth]);

  const isCheckedLength = useMemo(() => {
    const formattedValue = lengthAtBirth?.toString().replaceAll(',', '.');
    if (!formattedValue) return false;
    const isValidLength =
      parseFloat(formattedValue) >= 25 && parseFloat(formattedValue) <= 130;
    return formattedValue && formattedValue?.length < 6 && isValidLength;
  }, [lengthAtBirth]);

  const isCheckedHeight = useMemo(() => {
    const formattedValue = height?.toString().replaceAll(',', '.');
    if (!formattedValue) return false;
    const isValidHeight =
      parseFloat(formattedValue) >= 25 && parseFloat(formattedValue) <= 130;
    return formattedValue && formattedValue?.length < 6 && isValidHeight;
  }, [height]);

  const dialog = useDialog();

  const showInfo = () =>
    dialog({
      blocking: false,
      position: DialogPosition.Middle,
      color: 'bg-white',
      render: (onClose) => {
        return (
          <ActionModal
            className="z-50"
            icon="InformationCircleIcon"
            iconColor="infoMain"
            title="Growth monitoring"
            detailText={`Make sure your scale is working and place it on a flat hard surface. Weigh and measure ${name} without any clothes on (remove nappy, clothes, hats and socks).

          Weighing and measuring babies can help you identify children who are not growing well.`}
            actionButtons={[
              {
                colour: 'primary',
                text: 'Close',
                textColour: 'primary',
                type: 'outlined',
                leadingIcon: 'XIcon',
                onClick: onClose,
              },
            ]}
          />
        );
      },
    });

  useEffect(() => {
    if (
      !!weightAtBirth &&
      weightRegex.test(String(weightAtBirth).replaceAll(',', '.')) &&
      ((isDisplayHeight && height) ||
        !isDisplayHeight ||
        (isDisplayLength && lengthAtBirth) ||
        !isDisplayLength)
    ) {
      setSectionQuestions?.([
        {
          visitSection: weightAndLengthFormSection,
          questions: [
            {
              question: weightLengthAndHeightFormQuestions.weight,
              answer: String(weightAtBirth).replaceAll(
                ',',
                '.'
              ) as Question['answer'],
            },
            ...(isDisplayLength
              ? [
                  {
                    question: weightLengthAndHeightFormQuestions.length,
                    answer: String(lengthAtBirth).replaceAll(
                      ',',
                      '.'
                    ) as Question['answer'],
                  },
                ]
              : []),
            ...(isDisplayHeight
              ? [
                  {
                    question: weightLengthAndHeightFormQuestions.height,
                    answer: String(height).replaceAll(
                      ',',
                      '.'
                    ) as Question['answer'],
                  },
                ]
              : []),
          ],
        },
      ]);
    }

    if (isDisplayHeight || isDisplayLength) {
      if (
        isCheckedWeight &&
        ((isDisplayLength && isCheckedLength) ||
          (isDisplayHeight && isCheckedHeight))
      ) {
        return setEnableButton?.(true);
      } else {
        return setEnableButton?.(false);
      }
    } else {
      if (isCheckedWeight) {
        return setEnableButton?.(true);
      } else {
        return setEnableButton?.(false);
      }
    }
  }, [
    setEnableButton,
    setSectionQuestions,
    weightAtBirth,
    lengthAtBirth,
    isDisplayLength,
    isDisplayHeight,
    height,
    isCheckedWeight,
    isCheckedLength,
    isCheckedHeight,
  ]);

  return (
    <>
      <Header
        customIcon={P1}
        iconHexBackgroundColor={activitiesColours.pillar1.primaryColor}
        hexBackgroundColor={activitiesColours.pillar1.secondaryColor}
        title="Growth monitoring"
        subTitle={`Weight${isDisplayLength ? ' & length' : ''}${
          isDisplayHeight ? ' & height' : ''
        }`}
      />
      <div className="relative flex flex-col gap-3 p-4">
        <button className="absolute right-4" onClick={showInfo}>
          {renderIcon('InformationCircleIcon', 'h-6 w-6 text-infoMain')}
        </button>
        <div className="flex items-center gap-1">
          <FormInput
            register={register}
            nameProp={'weightAtBirth'}
            label={weightLengthAndHeightFormQuestions.weight}
            placeholder={'Tap to add'}
            type={'text'}
            min={0}
            maxLength={4}
          ></FormInput>
          <Typography
            type="body"
            color="textDark"
            text={'kg'}
            className="mt-7"
          />
        </div>
        {!!weightAtBirth && !isCheckedWeight && (
          <Alert
            className="mb-4"
            type="error"
            title="Please enter a valid weight between 1.5 and 30kg."
          />
        )}
        {isDisplayLength && (
          <>
            <div className="flex items-center gap-1">
              <FormInput
                register={register}
                nameProp={'lengthAtBirth'}
                label={weightLengthAndHeightFormQuestions.length}
                placeholder={'Tap to add'}
                type={'text'}
                min={0}
                maxLength={5}
              ></FormInput>
              <Typography
                type="body"
                color="textDark"
                text="cm"
                className="mt-7"
              />
            </div>
            {!!lengthAtBirth && !isCheckedLength && (
              <Alert
                className="mb-4"
                type="error"
                title="Please enter a valid length between 25 and 130cm."
              />
            )}
          </>
        )}
        {isDisplayHeight && (
          <>
            <div className="flex items-center gap-1">
              <FormInput
                register={register}
                nameProp={'height'}
                label={weightLengthAndHeightFormQuestions.height}
                placeholder={'Tap to add'}
                type={'text'}
                min={0}
                maxLength={5}
              ></FormInput>
              <Typography
                type="body"
                color="textDark"
                text="cm"
                className="mt-7"
              />
            </div>
            {!!height && !isCheckedHeight && (
              <Alert
                className="mb-4"
                type="error"
                title="Please enter a valid height between 25 and 130cm."
              />
            )}
          </>
        )}
      </div>
    </>
  );
};
